<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="detailedForm">
      <v-row>
        <v-col cols="12">
          <label>{{ $t('description') }}</label>
          <p class="label-info">
            A high-level description of the Provider in fairly non-technical terms, with the vision,
            mission, objectives, background, experience.
          </p>
          <tiptap-vuetify
            v-model="provider.description"
            :placeholder="'Max length is 2000 characters'"
            :extensions="extensions"
            class="required"
            :rules="requiredRule()"
          ></tiptap-vuetify>
          <div :class="charCount > maxChars ? 'red--text' : 'black--text'">
            {{ charCount }} / {{ maxChars }} characters
          </div>
        </v-col>
      </v-row>

      <!-- Optional -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <!-- Optional Social Media -->
                <v-row>
                  <v-col cols="12" xl="6" lg="6" md="6" sm="12">
                    <label>Logo</label>
                    <p class="label-info">
                      Link to the logo/visual identity of the Provider or if not available of the
                      Hosting Institution.
                    </p>
                    <v-text-field
                      v-model.trim="optionalProvider.logoUrl"
                      outlined
                      :placeholder="'Paste a URL'"
                      prepend-inner-icon="mdi-link-variant"
                      clearable
                      @input="handleLogoInput"
                    >
                    </v-text-field>
                    <v-alert v-if="!isValidLogoUrl" type="error" class="subtitle-2 py-2">
                      Please enter a valid URL
                    </v-alert>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <label>{{ $t('social-media') }}</label>
                    <p class="label-info">{{ $t('social-media-message') }}</p>
                    <v-row v-for="(socialMedia, i) in optionalProvider.socialMedia" :key="i">
                      <v-col cols="10" xl="5" lg="5" md="5" sm="10" class="pb-0">
                        <v-text-field
                          v-model="socialMedia.url"
                          :placeholder="$t('paste-url')"
                          outlined
                          :rules="requiredRule() && checkURL()"
                          class="required"
                          clearable
                          prepend-inner-icon="mdi-link-variant"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="10" xl="5" lg="5" md="5" sm="10" class="pb-0">
                        <v-select
                          v-model="socialMedia.type"
                          :items="[
                            'Twitter',
                            'Facebook',
                            'Instagram',
                            'LinkedIn',
                            'Youtube',
                            'Other',
                          ]"
                          outlined
                        ></v-select>
                      </v-col>
                      <v-col cols="2">
                        <v-btn fab small text @click="removeSocialLink(i)">
                          <v-icon>
                            $vuetify.icons.trash
                          </v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row v-if="edit">
                      <v-col cols="12">
                        <v-btn
                          @click="addSocialLink()"
                          depressed
                          rounded
                          large
                          color="primary200"
                          class="white--text font-weight-bold"
                        >
                          <v-icon left>
                            $vuetify.icons.plus
                          </v-icon>
                          <span
                            class="text-decoration-underline text-transform-none font-weight-bold"
                          >
                            {{ $t('add-social-link') }}
                          </span>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>

                <!-- Optional Certification -->
                <v-row
                  class="mt-15"
                  v-for="(certification, i) in optionalProvider.certifications"
                  :key="i"
                >
                  <v-col cols="10" xl="5" lg="5" md="5" sm="10">
                    <label>Certification</label>
                    <p class="label-info">
                      List of certifications obtained by the Provider <br />
                      (including the certification body, the certificate number).
                    </p>
                    <v-text-field
                      v-model="certification.certification"
                      outlined
                      :placeholder="'Tag'"
                      clearable
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="10" xl="5" lg="5" md="5" sm="10" v-if="certification.id">
                    <label>Certification Document</label>
                    <p class="label-info">Upload pdf<br /><br /></p>
                    <v-file-input
                      :disabled="!certification.certification"
                      v-model="certification.addFile"
                      outlined
                      :placeholder="'Upload pdf'"
                      clearable
                      prepend-icon=""
                      :multiple="false"
                    >
                    </v-file-input>
                    <div v-if="certification.certificationDocument">
                      <v-chip :close="true" @click:close="deleteCertification(certification)">
                        {{ certification.certificationDocument }}
                      </v-chip>
                    </div>
                    <div align="center">
                      <v-btn
                        @click="uploadCertification(certification)"
                        :disabled="!(certification.addFile && certification.certification)"
                        depressed
                        rounded
                        medium
                        color="primary200"
                        class="white--text font-weight-bold"
                        >Upload File
                        <v-icon color="white">mdi-upload</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                  <v-col cols="2" class="mt-16 pt-7 ">
                    <v-btn fab small text @click="removeCertification(i)">
                      <v-icon>
                        $vuetify.icons.trash
                      </v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row v-if="edit">
                  <v-col cols="12">
                    <label>Certification</label>
                    <p class="label-info">
                      List of certifications.
                    </p>
                    <v-btn
                      @click="createCertification()"
                      depressed
                      rounded
                      large
                      color="primary200"
                      class="white--text font-weight-bold"
                    >
                      <v-icon left>
                        $vuetify.icons.plus
                      </v-icon>
                      <span class="text-decoration-underline text-transform-none font-weight-bold">
                        {{ $t('add-certification') }}
                      </span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars"
          v-if="unsavedChanges && edit"
          @click="updateProviderDetailedInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          :disabled="charCount > maxChars"
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
        >
          {{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import EventBus from '@/helpers/event-bus.js';
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Strike,
  ListItem,
  BulletList,
  OrderedList,
} from 'tiptap-vuetify';

export default {
  name: 'AddProviderDetailedInfo',
  mixins: [util],
  components: { TiptapVuetify, AlertUnsavedChanges },
  data: () => ({
    provider: {
      description: '',
    },
    optionalProvider: {
      logoUrl: null,
      socialMedia: [],
      certifications: [],
    },
    unsavedChanges: false,
    dataLoaded: false,
    initialProvider: {},
    initialOptionalProvider: {},
    initialSocialMedia: {},
    initialCertifications: {},
    isValidLogoUrl: true,
    addFile: null,
    extensions: [Bold, Italic, Underline, Strike, ListItem, BulletList, OrderedList],
    maxChars: 2000,
  }),
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    provider: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;

        const descriptionChanged = this.provider.description !== this.initialProvider.description;

        this.unsavedChanges = descriptionChanged;
      },
    },
    optionalProvider: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.optionalProvider.logoUrl !== this.initialOptionalProvider.logoUrl;
      },
    },
    'optionalProvider.socialMedia': {
      deep: true,
      handler(newList) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newList) !== JSON.stringify(this.initialSocialMedia)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
    'optionalProvider.certifications': {
      deep: true,
      handler(newList) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newList) !== JSON.stringify(this.initialCertifications)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
  },
  mounted() {
    if (this.edit) {
      this.getProvider();
    }
  },
  computed: {
    charCount() {
      const div = document.createElement('div');
      div.innerHTML = this.provider.description || '';
      return div.textContent.length;
    },
  },
  methods: {
    stripHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    },
    next() {
      if (!this.provider.description || this.stripHtml(this.provider.description).trim() === '') {
        EventBus.$emit('snackbar', {
          text: this.$i18n.t('Description Required'),
          color: 'error',
        });
        return;
      }

      const isFormValid = this.$refs.detailedForm.validate();
      if (isFormValid && this.provider.description) {
        this.$emit('tab', 2);
        this.$emit('providerDetailedInfo', { ...this.provider });
      }
    },
    back() {
      this.$emit('tab', 0);
    },
    getProvider() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(res => {
          console.log('Get Provider Detailed Info', res.data);

          this.provider.description = res.data.description;
          this.optionalProvider.logoUrl = res.data.logoUrl;

          if (res.data.socialMedia) {
            this.optionalProvider.socialMedia = res.data.socialMedia.map(media => ({
              type: media.type,
              url: media.url,
            }));

            this.initialSocialMedia = JSON.parse(JSON.stringify(this.optionalProvider.socialMedia));
          }

          if (res.data.certifications) {
            this.optionalProvider.certifications = res.data.certifications.map(certification => ({
              id: certification.id,
              certification: certification.certification,
              certificationDocument: certification.certificationDocument,
            }));

            this.initialCertifications = JSON.parse(
              JSON.stringify(this.optionalProvider.certifications),
            );
          }

          this.initialProvider = {
            description: res.data.description,
          };

          this.initialOptionalProvider = {
            logoUrl: res.data.logoUrl,
          };

          this.dataLoaded = true;
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    cancel() {
      this.getProvider();
    },
    updateProviderDetailedInfo() {
      const id = this.$route.params.id;

      const providerUpdateData = {
        id: id,
        description: this.provider.description,
        logoUrl: this.optionalProvider.logoUrl,
        socialMedia: this.optionalProvider.socialMedia,
        certifications: this.optionalProvider.certifications,
      };

      this.deleteEmptyProperties(providerUpdateData);

      if (!this.provider.description || this.stripHtml(this.provider.description).trim() === '') {
        EventBus.$emit('snackbar', {
          text: this.$i18n.t('Description Required'),
          color: 'error',
        });
        return;
      }

      if (!this.isValidLogoUrl) {
        EventBus.$emit('snackbar', {
          text: this.$i18n.t('Valid Logo URL Required'),
          color: 'error',
        });
        return;
      }

      const isFormValid = this.$refs.detailedForm.validate();
      if (isFormValid) {
        this.axios
          .post('/api/v1/provider-settings/marketing-info', providerUpdateData)
          .then(response => {
            console.log('Provider info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Provider Updated Successfully',
              color: 'primary200',
            });
            this.initialProvider = { ...this.provider };
            this.initialOptionalProvider = { ...this.optionalProvider };
            this.getProvider();
            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);
          })
          .catch(error => {
            console.error('Error updating provider info:', error);
          });
      }
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
    handleLogoInput(value) {
      if (value) {
        this.validateLogoUrl(value);
      } else {
        this.isValidLogoUrl = true;
      }
    },
    validateLogoUrl() {
      this.isValidLogoUrl = this.canParse(this.optionalProvider.logoUrl);
    },
    canParse(link) {
      if (!/^https?:\/\//i.test(link)) {
        return false;
      }
      try {
        new URL(link);
        return true;
      } catch (e) {
        return false;
      }
    },
    addSocialLink() {
      this.optionalProvider.socialMedia.push({ url: null, type: 'Twitter' });
    },
    removeSocialLink(index) {
      this.optionalProvider.socialMedia.splice(index, 1);
    },
    createCertification() {
      this.optionalProvider.certifications.push({ certification: null });
    },
    removeCertification(index) {
      this.optionalProvider.certifications.splice(index, 1);
    },
    deleteCertification(certification) {
      const id = this.$route.params.id;

      const certificationId = certification.id;

      if (certification.certificationDocument) {
        const fileName = certification.certificationDocument;

        this.axios
          .put(`api/v1/provider-settings/${id}/file/${fileName}?certificationId=${certificationId}`)
          .then(response => {
            console.log('Certification deleted successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Certification deleted successfully!',
              color: 'primary200',
            });
            this.getProvider();
          })
          .catch(error => {
            console.error('Error deleting Certification:', error);
            EventBus.$emit('snackbar', {
              text: 'Failed to delete uCertification. Please try again.',
            });
          });
      } else {
        console.log('No Certification file to delete.');
      }
    },
    uploadCertification(certification) {
      const id = this.$route.params.id;

      const certificationId = certification.id;

      if (certification.addFile) {
        const fileType = certification.addFile.type;
        if (fileType !== 'application/pdf') {
          EventBus.$emit('snackbar', {
            text: 'Only PDF files are allowed!',
          });
          return;
        }

        const formData = new FormData();
        formData.append('type', certification.addFile.type);
        formData.append('name', certification.addFile.name);
        formData.append('size', certification.addFile.size);
        formData.append('file', certification.addFile);

        this.axios
          .put(`api/v1/provider-settings/${id}/file?certificationId=${certificationId}`, formData)
          .then(response => {
            console.log('Response Add File:', response.data);
            EventBus.$emit('snackbar', {
              text: 'File uploaded successfully!',
              color: 'primary200',
            });
            this.addFile = null;
            this.getProvider();
          })
          .catch(error => {
            console.log('Error:', error);
            EventBus.$emit('snackbar', {
              text: 'Failed to upload file. Please try again.',
            });
          });
      } else {
        console.log('Invalid file or id');
      }
    },
  },
};
</script>
